import { Backdrop, CircularProgress, Container } from "@mui/material";
import React, { useEffect, useState } from "react";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import Test from "../../components/Test";
import FeatureTests from "../../components/FeatureTests";
import { ApiCallStatus } from "../../utils/constants";
import { fetchEventDataByName } from "../PageFunctions/eventFunctions";
import { createEventResponse } from "../PageFunctions/formFunctions";
import { useArgusAuth } from "../../hooks/useArgusAuth";

const TestPage = ({ genericDataLoaded }) => {
  const { isAuthenticated, isLoading } = useArgusAuth();

  const [eventData, setEventData] = useState(null);
  const [fetchEventDataByNameCallStatus, setFetchEventDataByNameCallStatus] =
    useState(ApiCallStatus.NotStarted);
  const [createEventResponseCallStatus, setCreateEventResponseCallStatus] =
    useState(ApiCallStatus.NotStarted);

  const schema = yup
    .object({
      attending: yup
        .string()
        .required("Please indicate whether you are attending"),
      companyName: yup.string().required("Company name is required"),
      email: yup
        .string()
        .email("Email must be a valid email address")
        .required("Email is required"),
      accountNumber: yup.string(),
      guests: yup
        .number()
        .required("Please indicate how many guests you will be attending with")
        .min(1)
        .max(50),
    })
    .required();
  const {
    control,
    trigger,
    getValues,
    setValue,
    formState: { errors },
    clearErrors,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      attending: "Yes",
      companyName: "",
      email: "",
      accountNumber: "",
      guests: 1,
    },
  });

  const fetchEventDataByNameAsync = async () => {
    await fetchEventDataByName({
      eventName: "Trade Show 2025",
      setEventData,
      setStatusInProgress: () =>
        setFetchEventDataByNameCallStatus(ApiCallStatus.InProgress),
      setStatusSuccess: () =>
        setFetchEventDataByNameCallStatus(ApiCallStatus.Succeeded),
      setStatusFailed: () =>
        setFetchEventDataByNameCallStatus(ApiCallStatus.Failed),
      setStatusError: () =>
        setFetchEventDataByNameCallStatus(ApiCallStatus.Error),
    });
  };

  const createEventResponseAsync = async ({
    attending,
    companyName,
    email,
    accountNumber,
    guests,
  }) => {
    const eventResponse = {
      attending,
      companyName,
      email,
      accountNumber,
      guests,
    };

    await createEventResponse({
      eventId: eventData.eventId,
      eventResponse,
      setStatusInProgress: () =>
        setCreateEventResponseCallStatus(ApiCallStatus.InProgress),
      setStatusSuccess: () =>
        setCreateEventResponseCallStatus(ApiCallStatus.Succeeded),
      setStatusFailed: () =>
        setCreateEventResponseCallStatus(ApiCallStatus.Failed),
      setStatusError: () =>
        setCreateEventResponseCallStatus(ApiCallStatus.Error),
      setStatusDuplicateError: () =>
        setCreateEventResponseCallStatus(ApiCallStatus.Conflict),
    });
  };

  const unAuthorizedPageLoad = async () => {
    await fetchEventDataByNameAsync();
  };

  const onSubmitClick = async () => {
    const result = await trigger([
      "attending",
      "companyName",
      "email",
      "guests",
      "accountNumber",
    ]);
    if (!!result) {
      await createEventResponseAsync({
        attending: getValues("attending"),
        companyName: getValues("companyName"),
        email: getValues("email"),
        accountNumber: getValues("accountNumber")
          ? getValues("accountNumber")
          : null,
        guests: getValues("guests"),
      });
    }
  };

  useEffect(() => {
    if (genericDataLoaded === true && isLoading === false) {
      unAuthorizedPageLoad();
    }
  }, [genericDataLoaded, isLoading]);

  const allLoadingStates = [
    fetchEventDataByNameCallStatus,
    createEventResponseCallStatus,
  ];

  const pageIsLoading =
    allLoadingStates.includes(ApiCallStatus.InProgress) ||
    genericDataLoaded === false ||
    isLoading === true;

  return (
    <Container maxWidth="lg">
      <FeatureTests />
      {!!eventData && (
        <Test onSubmitClick={onSubmitClick} control={control} errors={errors} />
      )}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={pageIsLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Container>
  );
};
export default TestPage;
